import { z } from "zod";

/**
 * Seguimiento del despliegue (Fase 5). SOLO OBSERVACIÓN: la fábrica no despliega, no fusiona y no cambia nada en el proveedor; lee
 * evidencias y las registra con su fuente y su fecha. Lo que no se puede demostrar se muestra como pendiente, no disponible o no
 * confirmado, nunca como desplegado o verificado.
 */

/**
 * Fuente del resultado del pipeline de despliegue.
 *  - `none`: sin integración (opción sencilla). Solo se usan las comprobaciones HTTP que se configuren; sin ninguna, el despliegue no se observa.
 *  - `github_deployments`: API de Deployments de GitHub (despliegues y sus estados para un entorno). La escriben muchos proveedores o un
 *    workflow propio; si el proveedor no la escribe, no hay datos (se muestra como «no disponible»).
 */
export const DEPLOY_OBSERVERS = ["none", "github_deployments"] as const;
export type DeployObserver = (typeof DEPLOY_OBSERVERS)[number];

const httpsUrl = z.url({ protocol: /^https$/, hostname: z.regexes.domain }).max(500);

/** Configuración por repositorio. `null` en BD = sin configurar (el flujo de misiones no cambia). */
export const deploymentConfigSchema = z.object({
  observer: z.enum(DEPLOY_OBSERVERS).default("none"),
  /** Entorno de GitHub Deployments que se observa (`production`, `staging`…). */
  environment: z.string().trim().min(1).max(100).regex(/^[\w .:/-]+$/).default("production"),
  /** Comprobación de salud: GET que debe responder 2xx. */
  healthUrl: httpsUrl.nullable().default(null),
  /** Versión desplegada: GET que devuelve el commit en ejecución (texto plano o JSON con `versionField`). */
  versionUrl: httpsUrl.nullable().default(null),
  /** Ruta del campo con el commit dentro del JSON (`version`, `build.commit`…). Vacío = el cuerpo entero es el commit. */
  versionField: z.string().trim().max(100).regex(/^[A-Za-z_][\w-]*(\.[A-Za-z_][\w-]*)*$/).nullable().default(null),
  /** Prueba básica: GET con estado esperado y, opcionalmente, un texto que debe aparecer en la respuesta. */
  smokeUrl: httpsUrl.nullable().default(null),
  smokeExpectStatus: z.number().int().min(100).max(599).default(200),
  smokeExpectText: z.string().max(200).nullable().default(null),
  /** Ventana de observación tras el merge; pasado este tiempo lo que no se demostró queda «no confirmado». */
  windowMinutes: z.number().int().min(5).max(1440).default(120),
});
export type DeploymentConfig = z.infer<typeof deploymentConfigSchema>;
export const deploymentConfigInput = z.object({ config: deploymentConfigSchema.nullable() });

/** ¿La configuración permite observar algo? (sin pipeline ni comprobaciones no hay nada que observar). */
export const deploymentObservable = (c: DeploymentConfig | null | undefined): c is DeploymentConfig =>
  !!c && (c.observer !== "none" || !!c.healthUrl || !!c.versionUrl || !!c.smokeUrl);

/**
 * Estado persistido del seguimiento de una misión fusionada.
 *  - `merged`: fusionada; aún sin evidencia de despliegue de esa versión.
 *  - `deploying`: el proveedor informa de un despliegue en curso que incluye el commit fusionado.
 *  - `deployed`: hay evidencia de que la versión desplegada incluye el commit fusionado; la verificación falta o no es concluyente.
 *  - `verified`: desplegada y todas las comprobaciones configuradas (salud y/o prueba básica) superadas.
 *  - `deployment_failed`: el pipeline falló para esa versión, o la salud/prueba básica fallan de forma sostenida tras desplegarla.
 *  - `unconfirmed`: terminó la ventana sin poder demostrar que la versión fusionada está desplegada (o con evidencia contradictoria).
 */
export const DEPLOY_STATUSES = ["merged", "deploying", "deployed", "verified", "deployment_failed", "unconfirmed"] as const;
export type DeployStatus = (typeof DEPLOY_STATUSES)[number];

/** Estado que se muestra. Añade los casos sin seguimiento: desactivado, sin configuración, no fusionada (abierta o cerrada sin merge) y fusionada sin seguimiento. */
export const DEPLOY_VIEW_STATES = [...DEPLOY_STATUSES, "disabled", "not_configured", "not_merged", "closed_unmerged", "untracked"] as const;
export type DeployViewState = (typeof DEPLOY_VIEW_STATES)[number];

export const DEPLOY_STATUS_LABEL: Record<DeployViewState, string> = {
  merged: "Fusionada · despliegue pendiente",
  deploying: "Desplegando",
  deployed: "Desplegada",
  verified: "Verificada",
  deployment_failed: "Fallo de despliegue",
  unconfirmed: "Despliegue no confirmado",
  disabled: "Seguimiento desactivado",
  not_configured: "Despliegue sin configurar",
  not_merged: "Sin fusionar",
  closed_unmerged: "PR cerrada sin fusionar",
  untracked: "Fusionada · sin seguimiento",
};

/** Resultado de cada comprobación: distingue fallo, pendiente y dato no disponible (o no configurado), y el error al consultar. */
export const CHECK_RESULTS = ["pass", "fail", "pending", "unavailable", "error"] as const;
export type CheckResult = (typeof CHECK_RESULTS)[number];
export const DEPLOY_CHECK_KINDS = ["pipeline", "version", "health", "smoke"] as const;
export type DeployCheckKind = (typeof DEPLOY_CHECK_KINDS)[number];

/** Relación entre el commit fusionado y el desplegado (comparación de GitHub `merge...desplegado`). */
export const VERSION_RELATIONS = ["contains", "older", "diverged", "unknown"] as const;
export type VersionRelation = (typeof VERSION_RELATIONS)[number];
export const VERSION_MATCHES = ["confirmed", "mismatch", "unconfirmed"] as const;
export type VersionMatch = (typeof VERSION_MATCHES)[number];

export interface DeployCheck {
  result: CheckResult;
  /** Fuente concreta de la evidencia (`github_deployments:production#123`, `GET https://…/health`). */
  source: string | null;
  checkedAt: string | null;
  detail: string;
  /** Fallos consecutivos (salud y prueba básica: un fallo aislado durante el despliegue no se considera definitivo). */
  consecutiveFails?: number;
}

export interface MissionDeploymentDTO {
  enabled: boolean;
  configured: boolean;
  config: DeploymentConfig | null;
  state: DeployViewState;
  label: string;
  /** Causa legible del estado (qué falta, qué falló y con qué evidencia). */
  detail: string;
  reasonCode: string | null;
  pullRequest: { number: number; url: string; state: string } | null;
  mergeCommitSha: string | null;
  mergedAt: string | null;
  deployedSha: string | null;
  deploymentUrl: string | null;
  environment: string | null;
  versionMatch: VersionMatch | null;
  checks: Record<DeployCheckKind, DeployCheck> | null;
  observations: { used: number; max: number };
  startedAt: string | null;
  lastCheckedAt: string | null;
  nextCheckAt: string | null;
  deadlineAt: string | null;
  finishedAt: string | null;
  history: { at: string; type: string; actor: string; status: string | null; detail: string }[];
  canRecheck: boolean;
  recheckBlockedReason: string | null;
}

/** Resumen para la tarjeta del tablero (solo misiones completadas con seguimiento). */
export interface BoardDeploymentDTO {
  status: DeployStatus;
  label: string;
}
